/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * TdMarkdownEditor — Lit light-DOM wrapper around EasyMDE (#193).
 *
 * Used by `TdStoryForm`'s "What" tab so story content can be authored in
 * Markdown. The wrapper owns the EasyMDE lifecycle:
 *
 *   - `firstUpdated` instantiates EasyMDE on the one `<textarea>` the
 *     template renders. EasyMDE hides the textarea and inserts its own
 *     container as a sibling; Lit never touches those foreign nodes
 *     because the template has no bindings inside the editor region.
 *   - `value` is a reactive property. Editor → host: every CodeMirror
 *     change updates `value` and dispatches `markdown-change`
 *     (bubbles/composed, `detail: { value }`). Host → editor: assigning
 *     `value` programmatically writes through to the editor (guarded
 *     against echo loops by comparing current editor content).
 *   - `disconnectedCallback` tears the editor down via `toTextArea()`.
 *
 * No FontAwesome CDN: EasyMDE's stock toolbar wants FontAwesome classes
 * and offers to hot-load the CSS from a CDN (`autoDownloadFontAwesome`).
 * That's disabled — the toolbar below provides its own inline glyphs, so
 * the page makes no third-party requests.
 *
 * Preview rendering: the preview pane renders through `<wa-markdown>`
 * (WebAwesome), NOT EasyMDE's built-in marked pipeline — so the editor
 * preview and every read-only markdown display in the app share one
 * rendering pipeline. The markdown source reaches `<wa-markdown>` via
 * `script.textContent` (never an HTML parser), so `</script>` inside
 * user content cannot break out of the source element.
 */

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import EasyMDE from 'easymde';
import 'easymde/dist/easymde.min.css';
// Registers <wa-markdown>, the app-wide markdown display pipeline.
import '@awesome.me/webawesome-pro/dist/components/markdown/markdown.js';
import { t } from '../services/i18n.js';

/** Detail payload of the `markdown-change` event. */
export interface MarkdownChangeDetail {
  value: string;
}

/**
 * EasyMDE `previewRender` hook — renders through `<wa-markdown>`.
 *
 * EasyMDE assigns this function's return value to `preview.innerHTML`
 * AFTER the call, so the wa-markdown element is attached on the next
 * tick (the async-preview pattern from EasyMDE's docs) to avoid being
 * overwritten. The markdown travels as `textContent` of the
 * `<script type="text/markdown">` source node — assigned via DOM, never
 * parsed as HTML.
 */
const renderPreviewThroughWaMarkdown = (
  markdown: string,
  previewElement?: HTMLElement
): string => {
  if (!previewElement) return '';
  window.setTimeout(() => {
    const view = document.createElement('wa-markdown');
    const source = document.createElement('script');
    source.type = 'text/markdown';
    source.textContent = markdown;
    view.appendChild(source);
    previewElement.replaceChildren(view);
  }, 0);
  return t('mde.renderingPreview');
};

/**
 * Toolbar with self-contained inline glyphs (no FontAwesome CDN).
 * Actions are EasyMDE's own statics, so behaviour matches stock buttons.
 *
 * #89: built per instantiation (not a module constant) so the tooltip
 * titles resolve in the UI locale active when the editor mounts. An
 * already-mounted editor keeps its toolbar language until re-mounted —
 * EasyMDE offers no toolbar re-labelling API (noted in docs/i18n.md).
 */
const buildToolbar = (): readonly (EasyMDE.ToolbarIcon | '|')[] => [
  {
    name: 'bold',
    action: EasyMDE.toggleBold,
    className: 'td-mde-btn',
    icon: '<b>B</b>',
    title: t('mde.bold'),
  },
  {
    name: 'italic',
    action: EasyMDE.toggleItalic,
    className: 'td-mde-btn',
    icon: '<em>I</em>',
    title: t('mde.italic'),
  },
  {
    name: 'heading',
    action: EasyMDE.toggleHeadingSmaller,
    className: 'td-mde-btn',
    icon: '<b>H</b>',
    title: t('mde.heading'),
  },
  '|',
  {
    name: 'quote',
    action: EasyMDE.toggleBlockquote,
    className: 'td-mde-btn',
    icon: '<b>&#10077;</b>',
    title: t('mde.quote'),
  },
  {
    name: 'unordered-list',
    action: EasyMDE.toggleUnorderedList,
    className: 'td-mde-btn',
    icon: '<b>&#8226;&#8211;</b>',
    title: t('mde.bulletList'),
  },
  {
    name: 'ordered-list',
    action: EasyMDE.toggleOrderedList,
    className: 'td-mde-btn',
    icon: '<b>1.</b>',
    title: t('mde.numberedList'),
  },
  '|',
  {
    name: 'link',
    action: EasyMDE.drawLink,
    className: 'td-mde-btn',
    icon: '<b>&#128279;</b>',
    title: t('mde.insertLink'),
  },
  '|',
  {
    name: 'preview',
    action: EasyMDE.togglePreview,
    className: 'td-mde-btn no-disable',
    icon: '<b>&#128065;</b>',
    title: t('mde.preview'),
  },
];

@customElement('td-markdown-editor')
export class TdMarkdownEditor extends LitElement {
  /** Markdown source. Two-way: editor edits update it, assignments write through. */
  @property({ type: String }) value = '';
  /** Optional label rendered above the editor. */
  @property({ type: String }) label = '';
  /** Optional hint rendered below the editor. */
  @property({ type: String }) hint = '';
  /** Placeholder shown in the empty editor. */
  @property({ type: String }) placeholder = '';

  private _editor: EasyMDE | null = null;

  /** The underlying EasyMDE instance (null before first render). */
  get editor(): EasyMDE | null {
    return this._editor;
  }

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override firstUpdated(): void {
    const textarea = this.querySelector<HTMLTextAreaElement>(
      '.td-markdown-editor-host'
    );
    if (!textarea) return;
    this._editor = new EasyMDE({
      element: textarea,
      initialValue: this.value,
      autoDownloadFontAwesome: false,
      spellChecker: false,
      status: false,
      placeholder: this.placeholder,
      toolbar: [...buildToolbar()],
      previewRender: renderPreviewThroughWaMarkdown,
    });
    this._editor.codemirror.on('change', () => {
      const next = this._editor?.value() ?? '';
      if (next === this.value) return;
      this.value = next;
      this.dispatchEvent(
        new CustomEvent<MarkdownChangeDetail>('markdown-change', {
          detail: { value: next },
          bubbles: true,
          composed: true,
        })
      );
    });
  }

  override updated(changed: Map<PropertyKey, unknown>): void {
    // Host → editor write-through, guarded against the editor's own
    // change echo (the change handler above already synced `value`).
    if (
      changed.has('value') &&
      this._editor &&
      this._editor.value() !== this.value
    ) {
      this._editor.value(this.value);
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._editor?.toTextArea();
    this._editor = null;
  }

  override render(): TemplateResult {
    return html`
      ${
        this.label
          ? html`<label class="td-markdown-editor-label">${this.label}</label>`
          : nothing
      }
      <textarea class="td-markdown-editor-host"></textarea>
      ${
        this.hint
          ? html`<p class="td-markdown-editor-hint">${this.hint}</p>`
          : nothing
      }
    `;
  }
}
